import { z } from 'zod';

/**
 * Guest contact details from the QR self-order (ADR-0042). The restaurant is the data controller
 * under UU PDP (Law 27/2022): the name is needed to serve the order; the phone is optional and is
 * only used for promotions when the guest ticks a separate, unticked consent box.
 */

/** Version of the consent wording shown to the guest; stored with every consent. */
export const GUEST_MARKETING_CONSENT_VERSION = 'guest-marketing-v1';

/**
 * Phone number in E.164 (`+628123456789`), or null when it is not a plausible number.
 * Indonesian local forms are accepted: `0812…`, `62812…`, `+62 812-…`.
 */
export function normalizePhone(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  if (!/^\+?[\d\s().-]+$/.test(raw)) return null;
  let digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) {
    // Already international.
  } else if (digits.startsWith('0')) {
    digits = `62${digits.slice(1)}`;
  } else if (!digits.startsWith('62')) {
    return null;
  }
  // E.164: at most 15 digits; Indonesian mobiles are 62 + 9–12 digits.
  if (digits.length < 9 || digits.length > 15) return null;
  if (digits.startsWith('62') && (digits.length < 10 || digits[2] === '0')) return null;
  return `+${digits}`;
}

/** Name, optional phone and the separate marketing consent, sent with a guest order. */
export const guestContactInputShape = {
  guest_name: z.string().trim().min(1).max(60),
  phone: z
    .string()
    .trim()
    .max(24)
    .optional()
    .refine((v) => !v || normalizePhone(v) !== null, { message: 'Enter a valid phone number.' }),
  /** Explicit opt-in to promotions on that phone; never pre-ticked. */
  marketing_consent: z.boolean().optional(),
};

export const guestContactSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string(),
  marketing_consent: z.boolean(),
  consent_at: z.string().nullable(),
  consent_version: z.string().nullable(),
  consent_withdrawn_at: z.string().nullable(),
  order_count: z.number().int(),
  first_order_at: z.string(),
  last_order_at: z.string(),
  version: z.number().int(),
});
export type GuestContact = z.infer<typeof guestContactSchema>;

export const guestContactListSchema = z.object({ items: z.array(guestContactSchema) });

export const withdrawGuestConsentRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
